<div
  class="edit-disabled-zone"
  [class.edit-disabled-zone--active]="disabled"
  [attr.inert]="disabled ? '' : null"
  [attr.aria-disabled]="disabled"
>
  <section class="edit-readonly-section">
    <h4 class="edit-list-title">Member list ({{ memberTotal | number }})</h4>
    <div class="edit-list">
      @if (memberTotal === 0) {
      <div class="edit-list-empty">{{ 'readonly.noMembers' | translate }}</div>
      }
      @for (member of members; track member.email) {
      <div class="edit-list-item">
        @if (getMemberAvatarUrl(member)) {
        <img
          [src]="getMemberAvatarUrl(member)"
          [alt]="member.name"
          class="edit-list-avatar edit-list-avatar-image"
          (error)="onMemberAvatarError(member)"
        />
        } @else {
        <div class="edit-list-avatar">{{ member.name.charAt(0).toUpperCase() }}</div>
        }
        <div class="edit-list-content">
          <span class="edit-list-primary">{{ member.name }}</span>
          <span class="edit-list-secondary edit-list-email">{{ member.email }}</span>
        </div>
        @if (member.role) {
        <span class="edit-admin-badge" [class.edit-admin-badge--main]="member.role === 'Main admin'">
          {{ member.role }}
        </span>
        }
      </div>
      }
    </div>
    @if (canToggleMembers) {
    <button type="button" class="edit-list-more-btn" (click)="toggleMembers.emit()">
      @if (membersExpanded) {
      Show less
      <i class="pi pi-chevron-up"></i>
      } @else {
      + {{ membersMoreCount | number }} more
      <i class="pi pi-chevron-down"></i>
      }
    </button>
    }
  </section>

  <section class="edit-readonly-section">
    <h4 class="edit-list-title">Subgroup list ({{ subgroupTotal }})</h4>
    <div class="edit-list">
      @if (subgroupTotal === 0) {
      <div class="edit-list-empty">{{ 'readonly.noSubgroups' | translate }}</div>
      }
      @for (subgroup of subgroups; track subgroup.id) {
      <div class="edit-list-expandable">
        <div class="edit-list-item edit-list-item--nav edit-list-item--rich edit-list-item--subgroup" (click)="openSubgroup(subgroup, $event)">
          @if (hasThumbnailUrl(subgroup)) {
          <img
            [src]="subgroup.thumbnailUrl"
            [alt]="subgroup.name"
            class="edit-list-thumb"
            (error)="onSubgroupThumbnailError(subgroup)"
          />
          } @else {
          <div class="edit-list-thumb edit-list-thumb-placeholder" aria-hidden="true">
            <img [src]="getSportIconPath(subgroup.sportIcon || subgroup.sport)" class="edit-list-thumb-placeholder-icon" alt="" />
          </div>
          }
          <div class="edit-list-content edit-list-content--rich">
            <div class="edit-list-title-row">
              <span class="edit-list-primary">{{ subgroup.name }}</span>
              <span class="edit-subgroup-badge">{{ 'readonly.subGroup' | translate }}</span>
            </div>
            <div class="edit-list-meta-grid edit-list-meta-grid--subgroup">
              <span class="edit-list-meta-item"><i class="pi pi-map-marker"></i><span class="edit-list-meta-text">{{ subgroup.location }}</span></span>
              <span class="edit-list-meta-item"><img [src]="getSportIconPath(subgroup.sport)" class="edit-list-sport-icon" alt="" /><span class="edit-list-meta-text">{{ subgroup.sport }}</span></span>
              <span class="edit-list-meta-item"><i class="pi pi-users"></i><span class="edit-list-meta-text">{{ subgroup.members }} members</span></span>
              <span class="edit-list-meta-item"><i class="pi pi-sitemap"></i><span class="edit-list-meta-text">{{ subgroup.activities }} subgroups</span></span>
            </div>
          </div>
          <button type="button" class="edit-list-expand-btn" (click)="openSubgroup(subgroup, $event)">
            <i class="pi pi-chevron-right"></i>
          </button>
        </div>
      </div>
      }
    </div>
    @if (canToggleSubgroups) {
    <button type="button" class="edit-list-more-btn" (click)="toggleSubgroups.emit()">
      @if (subgroupsExpanded) {
      Show less
      <i class="pi pi-chevron-up"></i>
      } @else {
      + {{ subgroupsMoreCount }} more
      <i class="pi pi-chevron-down"></i>
      }
    </button>
    }
  </section>

  <section class="edit-readonly-section">
    <h4 class="edit-list-title">Activities ({{ activityTotal }})</h4>
    @if (activityTotal > 0) {
    <div class="edit-activity-status-tabs" role="tablist" aria-label="Activity status">
      @for (tab of activityStatusTabs; track tab.value) {
      <button
        type="button"
        class="edit-activity-status-tab"
        [class.edit-activity-status-tab--active]="activeActivityStatus === tab.value"
        role="tab"
        [attr.aria-selected]="activeActivityStatus === tab.value"
        (click)="setActiveActivityStatus(tab.value)"
      >
        {{ tab.label | translate }} ({{ getActivityStatusCount(tab.value) }})
      </button>
      }
    </div>
    }
    <div class="edit-list">
      @if (activityTotal === 0) {
      <div class="edit-list-empty">{{ 'readonly.noActivities' | translate }}</div>
      }
      @if (activityTotal > 0 && getActivityStatusCount(activeActivityStatus) === 0) {
      <div class="edit-list-empty">No {{ activeActivityStatus }} activities in this group yet</div>
      }
      @for (activity of getVisibleActivitiesByStatus(); track activity.id) {
      <div class="edit-list-expandable">
        <div class="edit-list-item edit-list-item--nav edit-list-item--rich edit-list-item--activity" (click)="openActivity(activity, $event)">
          @if (hasThumbnailUrl(activity)) {
          <img
            [src]="activity.thumbnailUrl"
            [alt]="activity.name"
            class="edit-list-thumb edit-list-thumb--activity"
            (error)="onActivityThumbnailError(activity)"
          />
          } @else {
          <div class="edit-list-thumb edit-list-thumb--activity edit-list-thumb-placeholder" aria-hidden="true">
            <img [src]="getSportIconPath(activity.sportIcon || activity.sport)" class="edit-list-thumb-placeholder-icon" alt="" />
          </div>
          }
          <div class="edit-list-content edit-list-content--rich">
            <span class="edit-list-secondary edit-list-date"><i class="pi pi-calendar"></i>{{ activity.date }} &bull; {{ activity.time }}</span>
            <span class="edit-list-primary edit-list-primary--activity">{{ activity.name }}</span>
            <div class="edit-list-meta-grid edit-list-meta-grid--activity">
              <span class="edit-list-meta-item">
                <img src="assets/icons/activity-location.svg" class="edit-list-activity-meta-icon" alt="" />
                <span class="edit-list-meta-text">{{ activity.location }}</span>
              </span>
              <span class="edit-list-meta-item edit-list-level">
                <img src="assets/icons/level-ribbon.svg" class="edit-list-activity-meta-icon edit-list-level-icon" alt="" />
                <span class="edit-list-meta-text">{{ getActivityLevelLabel(activity) }}</span>
              </span>
              <span class="edit-list-meta-item edit-list-activity-type">
                <img [src]="getSportIconPath(activity.sportIcon || activity.sport)" class="edit-list-activity-meta-icon edit-list-activity-sport-icon" alt="" />
                <span class="edit-list-meta-text">{{ getActivityTypeLabel(activity) }}</span>
              </span>
            </div>
          </div>
          <button type="button" class="edit-list-expand-btn" (click)="openActivity(activity, $event)">
            <i class="pi pi-chevron-right"></i>
          </button>
        </div>
      </div>
      }
    </div>
    @if (canToggleActiveActivities()) {
    <button type="button" class="edit-list-more-btn" (click)="toggleActivities.emit(activeActivityStatus)">
      @if (activitiesExpanded) {
      Show less
      <i class="pi pi-chevron-up"></i>
      } @else {
      + {{ getActiveActivitiesMoreCount() }} more
      <i class="pi pi-chevron-down"></i>
      }
    </button>
    }
  </section>
</div>
